import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Accordion from "react-bootstrap/Accordion";
import { Form, Spinner } from "react-bootstrap";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { toast } from "react-toastify";
import axios from "axios";

function CheckpointModal({ show, handleClose, fsrId, categoryId }) {
  const [fieldInputs, setFieldInputs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchExistingCheckpoints();
    }
  }, [show]);

  const fetchExistingCheckpoints = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/fsrs/`, {
        headers: HEADERS,
        params: { fsr_id: fsrId, category_id: categoryId },
      });

      if (Array.isArray(response.data)) {
        const existingCheckpoints = response.data.map((checkpoint) => ({
          id: checkpoint.id,
          value: checkpoint.checkpoint_name || "",
          checkpointStatuses: checkpoint.checkpointStatuses.length
            ? checkpoint.checkpointStatuses
            : [""],
          displayType: checkpoint.displayType || "text",
        }));
        setFieldInputs(existingCheckpoints);
      } else {
        toast.error("Invalid data format received from the server.");
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddFieldInput = () => {
    const nextFieldNumber = fieldInputs.length
      ? fieldInputs[fieldInputs.length - 1].id + 1
      : 1;
    setFieldInputs([
      ...fieldInputs,
      {
        id: nextFieldNumber,
        value: "",
        checkpointStatuses: [""],
        displayType: "text",
      },
    ]);
  };

  const handleRemoveFieldInput = (index) => {
    const newInputs = [...fieldInputs];
    newInputs.splice(index, 1);
    setFieldInputs(newInputs);
  };

  const handleFieldInputChange = (value, index, field) => {
    const newInputs = [...fieldInputs];
    newInputs[index][field] = value;
    setFieldInputs(newInputs);
  };

  const handleCheckpointStatusChange = (
    value,
    checkpointIndex,
    statusIndex
  ) => {
    const newInputs = [...fieldInputs];
    newInputs[checkpointIndex].checkpointStatuses[statusIndex] = value;
    setFieldInputs(newInputs);
  };

  const handleAddCheckpointStatus = (index) => {
    const newInputs = [...fieldInputs];
    newInputs[index].checkpointStatuses.push("");
    setFieldInputs(newInputs);
  };

  const handleRemoveCheckpointStatus = (checkpointIndex, statusIndex) => {
    const newInputs = [...fieldInputs];
    newInputs[checkpointIndex].checkpointStatuses.splice(statusIndex, 1);
    setFieldInputs(newInputs);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const invalidDropdowns = fieldInputs.some((field) => {
      if (field.displayType === "dropdown") {
        return field.checkpointStatuses.some((status) => status.trim() === "");
      }
      return false;
    });

    if (invalidDropdowns) {
      toast.error("Please fill all checkpoint statuses for dropdown fields.");
      return;
    }

    const checkpoints = fieldInputs
      .filter((input) => input.value.trim() !== "")
      .map(({ value, checkpointStatuses, displayType }) => ({
        checkpoint_name: value.trim(),
        checkpointStatuses: checkpointStatuses.filter(
          (status) => status.trim() !== ""
        ),
        displayType,
      }));

    if (checkpoints.length === 0) {
      toast.error("Please add and fill all checkpoint fields.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fsr_id: fsrId,
        category_id: categoryId,
        checkpoints,
      };
      await axios.post(`${BASE_URL}/api/add-checkpoints/`, payload, {
        headers: HEADERS,
      });
      toast.success("Checkpoints added successfully!");
      handleClose();
    } catch (error) {
      console.error("Error saving checkpoints. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Checkpoint</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleFormSubmit}>
        <Modal.Body
          style={{
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <Spinner animation="border" />
            </div>
          ) : (
            <Accordion defaultActiveKey="0" className="mb-3">
              {fieldInputs.map((field, index) => (
                <Accordion.Item eventKey={index.toString()} key={index}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center w-100">
                      <Form.Control
                        type="text"
                        value={field.value}
                        onChange={(e) =>
                          handleFieldInputChange(e.target.value, index, "value")
                        }
                        placeholder={`Checkpoint Name`}
                        className="me-2"
                        style={{ maxWidth: "75%", borderRadius: "0.25rem" }}
                      />
                      {index > 0 && (
                        <Button
                          variant="outline-danger"
                          onClick={() => handleRemoveFieldInput(index)}
                          size="sm"
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: "0.85rem" }}>
                        Checkpoint Status Type
                      </Form.Label>
                      <div className="d-flex justify-content-around">
                        {["text", "dropdown", "calendar"].map((type) => (
                          <Form.Check
                            inline
                            type="radio"
                            key={type}
                            label={type.charAt(0).toUpperCase() + type.slice(1)}
                            name={`displayType-${index}`}
                            checked={field.displayType === type}
                            onChange={() =>
                              handleFieldInputChange(type, index, "displayType")
                            }
                            className="me-1"
                          />
                        ))}
                      </div>
                    </Form.Group>

                    {field.displayType === "dropdown" && (
                      <>
                        {field.checkpointStatuses.map((status, statusIndex) => (
                          <div
                            key={statusIndex}
                            className="d-flex align-items-center mb-2"
                          >
                            <Form.Control
                              type="text"
                              placeholder="Checkpoint Status"
                              value={status}
                              onChange={(e) =>
                                handleCheckpointStatusChange(
                                  e.target.value,
                                  index,
                                  statusIndex
                                )
                              }
                              className="me-2"
                              style={{ flex: 1 }}
                            />
                            {statusIndex > 0 && (
                              <Button
                                variant="outline-danger"
                                onClick={() =>
                                  handleRemoveCheckpointStatus(
                                    index,
                                    statusIndex
                                  )
                                }
                                size="sm"
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="link"
                          onClick={() => handleAddCheckpointStatus(index)}
                          className="mb-2 p-0"
                          style={{ fontSize: "0.85rem" }}
                        >
                          + Add Status
                        </Button>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
          {!loading && (
            <Button
              variant="primary"
              onClick={handleAddFieldInput}
              className="w-100 mb-3"
            >
              + Add Checkpoint
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              "Submit"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default CheckpointModal;
